import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { alertActions } from '../../store/alert';
import { AuthContext } from '../context/auth-context';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AlertDialog from '../utilities/AlertDialog';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import TimecardsAPI from '../../services/api/timecards/timecards-api';
import PackagesAPI from '../../services/api/admin/packages-api';
import Logger from '../../utils/logger';
import './timecardadditem.css';
import CopyPreviousEntryDialog from './CopyPreviousEntryDialog';

const TimecardAddItem = (props) => {
  const {
    activeProjects,
    activePackages,
    activityOptions,
    addTimeItem,
    addTimeItems,
    areas,
    setAreas,
    timeCardUser,
    timeCardForeman,
    timeCardDate,
  } = props;

  const [projectPackages, setProjectPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState([]); // Areas filtered to only show those with available packages
  const [packageAct, setPackageAct] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [copyPreviousDialogOpen, setCopyPreviousDialogOpen] = useState(false);
  const [newTimecard, setNewTimecard] = useState({
    project: '',
    package: '',
    activity: '',
    hours_st: 0,
    hours_ot: 0,
    hours_dt: 0,
    shift: null,
    exception: '',
    payrollNotes: '',
  });

  // Refs to access NumericTextBoxComponent values directly
  const hoursStRef = useRef(null);
  const hoursOtRef = useRef(null);
  const hoursDtRef = useRef(null);
  const previousLocationTypeRef = useRef(null);

  const dispatch = useDispatch();
  const auth = useContext(AuthContext);

  // Determine location type from packages
  // Shop employees only see packages with requires_shop_fabrication = true
  // If all packages have requires_shop_fabrication = true, it's a shop employee
  const currentLocationType = useMemo(() => {
    if (!activePackages || activePackages.length === 0) {
      return null;
    }
    // Check if all packages require shop fabrication (indicates shop employee)
    const allShopPackages = activePackages.every(
      (pkg) => pkg.requires_shop_fabrication === true
    );
    return allShopPackages ? 'shop' : 'field';
  }, [activePackages]);

  // Reset exception and payroll notes when user or foreman changes
  useEffect(() => {
    setNewTimecard((prev) => ({
      ...prev,
      exception: '',
      payrollNotes: '',
    }));
  }, [timeCardUser?.id, timeCardForeman?.id]);

  // Reset everything when location type changes (shop vs field)
  useEffect(() => {
    // Only reset if location type actually changed (not on initial mount)
    if (
      previousLocationTypeRef.current !== null &&
      previousLocationTypeRef.current !== currentLocationType
    ) {
      setNewTimecard({
        project: '',
        package: '',
        activity: '',
        hours_st: 0,
        hours_ot: 0,
        hours_dt: 0,
        shift: null,
        exception: '',
        payrollNotes: '',
      });
      setProjectPackages([]);
      setFilteredPackages([]);
      setFilteredAreas([]);
      setPackageAct([]);
      setSelectedArea(null);
    }
    // Update the ref to track current location type
    previousLocationTypeRef.current = currentLocationType;
  }, [currentLocationType]);

  // Function to copy all previous day timecards
  const copyPreviousDayTimecards = async (includeHours = true) => {
    if (!timeCardUser?.id || !auth.token) {
      dispatch(
        alertActions.showAlert({
          title: 'Error',
          message:
            'Unable to copy previous day timecards. User information is missing.',
        })
      );
      return;
    }

    try {
      const previousDayData = await TimecardsAPI.getPreviousDayTimecard(
        timeCardUser.id,
        timeCardDate,
        auth.token
      );

      const previousTimecards = previousDayData.previousTimecards || [];

      if (previousTimecards.length === 0) {
        dispatch(
          alertActions.showAlert({
            title: 'No Previous Timecards',
            message: 'No timecard entries found for the previous day.',
          })
        );
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      const timecardsToAdd = [];

      // Process each timecard entry
      for (const previousTimecard of previousTimecards) {
        try {
          // Find matching project
          const matchingProject = activeProjects.find(
            (p) => p.id === previousTimecard.project_id
          );

          if (!matchingProject) {
            errorCount++;
            errors.push(`Project not found for entry`);
            continue;
          }

          // Find matching package
          const matchingPackage = activePackages.find(
            (p) => p.package_id === previousTimecard.package_id
          );

          if (!matchingPackage) {
            errorCount++;
            errors.push(`Package not found for entry`);
            continue;
          }

          // Find matching activity
          const matchingActivity = activityOptions.find(
            (a) => a.activity_id === previousTimecard.activity_id
          );

          if (!matchingActivity) {
            errorCount++;
            errors.push(`Activity not found for entry`);
            continue;
          }

          // Create timecard object in the format expected by addTimeItems
          const timecardToAdd = {
            project: matchingProject,
            package: matchingPackage,
            activity: matchingActivity,
            hours_st: includeHours
              ? parseFloat(previousTimecard.hours_st || 0)
              : 0,
            hours_ot: includeHours
              ? parseFloat(previousTimecard.hours_ot || 0)
              : 0,
            hours_dt: includeHours
              ? parseFloat(previousTimecard.hours_dt || 0)
              : 0,
            shift: previousTimecard.shift || null,
            exception: previousTimecard.exception || '',
            payrollNotes: '', // Don't copy notes from previous day
            union: previousTimecard.union || null,
            is_union_override: previousTimecard.is_union_override || false,
          };

          timecardsToAdd.push(timecardToAdd);
        } catch (err) {
          errorCount++;
          errors.push(`Error adding entry: ${err.message}`);
        }
      }

      if (timecardsToAdd.length > 0) {
        // Add all timecards at once
        await addTimeItems(timecardsToAdd);
        successCount = timecardsToAdd.length;
      }

      // Show error alert only if all failed
      if (successCount === 0 && errorCount > 0) {
        dispatch(
          alertActions.showAlert({
            title: 'Error',
            message: `Failed to copy timecards. ${errors.join('; ')}`,
          })
        );
      }
    } catch (err) {
      dispatch(
        alertActions.showAlert({
          title: 'Error Copying Timecards',
          message: `There was an error copying the previous day timecards: ${err.message}`,
        })
      );
    }
  };

  const handleChange = (e) => {
    const type = e.target.type;
    let value =
      type === 'number'
        ? parseFloat(e.target.value).toFixed(1)
        : e.target.value;

    if (e.target.name === 'project') {
      if (value?.id) {
        const projPackages = activePackages.filter((pckg) => {
          if (pckg.project_id === value.id) {
            return true;
          } else {
            return false;
          }
        });

        setProjectPackages(projPackages);
        setFilteredPackages(projPackages);

        // Fetch areas for the selected project
        if (auth.token && setAreas) {
          const fetchAreas = async () => {
            try {
              const options = await PackagesAPI.getPackageOptions(
                value.id,
                auth.token
              );
              const allAreas = options.packageAreas || [];
              setAreas(allAreas);

              // Filter areas to only show those that have at least one available package
              // This ensures shop employees only see areas with shop packages
              const areasWithPackages = allAreas.filter((area) => {
                return projPackages.some(
                  (pkg) => pkg.area === area.name || pkg.area_id === area.id
                );
              });
              setFilteredAreas(areasWithPackages);
              Logger.debug('Filtered areas based on available packages', {
                allAreasCount: allAreas.length,
                filteredAreasCount: areasWithPackages.length,
                projectPackagesCount: projPackages.length,
              });
            } catch (err) {
              Logger.error('Error fetching areas for project', err, {
                projectId: value.id,
              });
              setAreas([]);
              setFilteredAreas([]);
            }
          };
          fetchAreas();
        }
      }
      setPackageAct([]);
      setSelectedArea(null);

      setNewTimecard({
        ...newTimecard,
        [e.target.name]: value,
        package: '',
        activity: '',
      });
    } else if (e.target.name === 'packageArea') {
      setSelectedArea(value);

      if (value?.name) {
        const areaFilteredPackages = projectPackages.filter((pckg) => {
          const pckgAreaName = pckg.area?.toString().trim();
          const selectedAreaName = value.name?.toString().trim();
          return pckgAreaName === selectedAreaName;
        });
        setFilteredPackages(areaFilteredPackages);
      } else {
        setFilteredPackages(projectPackages);
      }

      setNewTimecard({
        ...newTimecard,
        package: '',
        activity: '',
      });
    } else if (e.target.name === 'package') {
      if (value?.archived) {
        dispatch(
          alertActions.showAlert({
            title: 'Package is Archived',
            message:
              'This work package has been archived and is no longer accepting time entries. If you need to charge time to this package, please contact an administrator to have it unarchived.',
          })
        );
        setNewTimecard({
          ...newTimecard,
          package: '',
          activity: '',
        });
        return;
      }

      // When user selects a package without having selected an area first,
      // backward-select the area that this package belongs to
      if (value && (value.area_id || value.area) && filteredAreas?.length) {
        const matchingArea = filteredAreas.find(
          (area) =>
            area.id === value.area_id ||
            area.name?.toString().trim() === value.area?.toString().trim()
        );
        if (matchingArea) {
          setSelectedArea(matchingArea);
          const areaFilteredPackages = projectPackages.filter((pckg) => {
            const pckgAreaName = pckg.area?.toString().trim();
            const areaName = matchingArea.name?.toString().trim();
            return (
              pckgAreaName === areaName || pckg.area_id === matchingArea.id
            );
          });
          setFilteredPackages(areaFilteredPackages);
        }
      }

      // Filter activities based on the package's saved activities (from package_activities table)
      // Use activity_ids array which contains UUIDs of allowed activities
      if (
        value?.activity_ids &&
        Array.isArray(value.activity_ids) &&
        value.activity_ids.length > 0
      ) {
        const filteredActivities = activityOptions.filter((activity) =>
          value.activity_ids.includes(activity.activity_id)
        );
        Logger.debug('Filtering activities for package', {
          packageName: value.package_name,
          activityIds: value.activity_ids,
          filteredCount: filteredActivities.length,
        });
        setPackageAct(filteredActivities);
      } else {
        // If no activities defined for package, show all activities
        setPackageAct(activityOptions);
      }

      // Check if package is in Single Activity Mode (include_activities = false)
      // If so, auto-select the default activity
      let autoSelectedActivity = '';
      if (value?.include_activities === false && value?.default_activity_id) {
        // Find the matching activity object from activityOptions
        const defaultActivity = activityOptions.find(
          (a) => a.activity_id === value.default_activity_id
        );
        if (defaultActivity) {
          autoSelectedActivity = defaultActivity;
          Logger.debug(
            'Single Activity Mode - auto-selecting default activity',
            {
              activityId: defaultActivity.activity_id,
              activityName: defaultActivity.activity_name,
            }
          );
        }
      }

      setNewTimecard({
        ...newTimecard,
        [e.target.name]: value,
        activity: autoSelectedActivity,
      });
    } else if (e.target.name === 'shift') {
      // Convert empty string to null for shift
      setNewTimecard({
        ...newTimecard,
        shift: value === '' ? null : value,
      });
    } else {
      setNewTimecard({
        ...newTimecard,
        [e.target.name]: value,
      });
    }
  };

  // Helper function to get value from NumericTextBoxComponent ref
  const getValueFromNumericRef = (ref, fieldId) => {
    if (!ref) return null;

    // Try multiple ways to access the value from Syncfusion component
    // Method 1: Direct value property
    if (ref.value !== undefined && ref.value !== null) {
      return parseFloat(ref.value) || 0;
    }

    // Method 2: Access through element property
    if (ref.element) {
      // Try element.value
      if (ref.element.value !== undefined && ref.element.value !== null) {
        return parseFloat(ref.element.value) || 0;
      }

      // Try to find input element within the component
      const inputElement = ref.element.querySelector('input');
      if (
        inputElement &&
        inputElement.value !== undefined &&
        inputElement.value !== ''
      ) {
        return parseFloat(inputElement.value) || 0;
      }
    }

    // Method 3: Try to find by ID in DOM (fallback)
    if (fieldId) {
      const domElement = document.getElementById(fieldId);
      if (domElement) {
        const inputElement = domElement.querySelector('input');
        if (
          inputElement &&
          inputElement.value !== undefined &&
          inputElement.value !== ''
        ) {
          return parseFloat(inputElement.value) || 0;
        }
      }
    }

    return null;
  };

  // Handler to capture value on blur (when keyboard is dismissed)
  // Uses a small delay to ensure value is available (important for iPad keyboard dismissal)
  const handleNumericBlur = (fieldName, e) => {
    // Get the ref for this field
    const ref =
      fieldName === 'hours_st'
        ? hoursStRef.current
        : fieldName === 'hours_ot'
          ? hoursOtRef.current
          : hoursDtRef.current;

    // Try to get value immediately from event
    let immediateValue = null;
    if (e && e.value !== undefined && e.value !== null) {
      immediateValue = parseFloat(e.value) || 0;
    } else if (e && e.target && e.target.value !== undefined) {
      immediateValue = parseFloat(e.target.value) || 0;
    }

    // Also try to get from ref immediately
    const refValue = getValueFromNumericRef(ref, fieldName);

    // Use immediate value if available, otherwise use ref value
    const value =
      immediateValue !== null
        ? immediateValue
        : refValue !== null
          ? refValue
          : 0;

    // Update state immediately with what we have
    if (value !== 0 || immediateValue !== null || refValue !== null) {
      setNewTimecard((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    }

    // Use requestAnimationFrame + setTimeout to check again after a brief delay
    // This handles cases where the value isn't immediately available (iPad keyboard dismissal)
    requestAnimationFrame(() => {
      setTimeout(() => {
        const delayedRefValue = getValueFromNumericRef(ref, fieldName);
        if (delayedRefValue !== null && delayedRefValue !== value) {
          setNewTimecard((prev) => ({
            ...prev,
            [fieldName]: delayedRefValue,
          }));
        }
      }, 50); // Small delay to allow Syncfusion to update its internal state
    });
  };

  const handleSubmit = () => {
    if (!timeCardForeman) {
      dispatch(
        alertActions.showAlert({
          title: 'Select a Foreman',
          message: 'A foreman must be selected before adding timecard items.',
        })
      );
      return;
    }

    // Before submitting, ensure we have the latest values from the numeric inputs
    // This is especially important for iPad when keyboard is dismissed
    const finalTimecard = { ...newTimecard };

    // Get current values from refs as fallback to ensure we have the latest values
    const stValue = getValueFromNumericRef(hoursStRef.current, 'hours_st');
    const otValue = getValueFromNumericRef(hoursOtRef.current, 'hours_ot');
    const dtValue = getValueFromNumericRef(hoursDtRef.current, 'hours_dt');

    if (stValue !== null) finalTimecard.hours_st = stValue;
    if (otValue !== null) finalTimecard.hours_ot = otValue;
    if (dtValue !== null) finalTimecard.hours_dt = dtValue;

    if (finalTimecard.project) {
      if (finalTimecard.package) {
        // Check if package has include_activities = false (activities excluded)
        const activitiesExcluded =
          finalTimecard.package?.include_activities === false;

        if (activitiesExcluded || finalTimecard.activity) {
          addTimeItem(finalTimecard);
          setNewTimecard({
            ...finalTimecard,
            hours_st: 0,
            hours_ot: 0,
            hours_dt: 0,
            // Keep the exception, payrollNotes, and shift values instead of resetting
          });
        } else {
          Logger.debug('Showing alert for activity validation');
          dispatch(
            alertActions.showAlert({
              title: 'Add an Activity',
              message: 'The activity cannot be blank.',
            })
          );
        }
      } else {
        dispatch(
          alertActions.showAlert({
            title: 'Add a Package',
            message: 'The package cannot be blank.',
          })
        );
      }
    } else {
      dispatch(
        alertActions.showAlert({
          title: 'Add a Project',
          message: 'The project cannot be blank.',
        })
      );
    }
  };

  // Create a custom Popper component with extended width
  const CustomPopper = (props) => {
    return (
      <Popper
        {...props}
        placement="bottom"
        style={{
          width: 'auto', // This allows it to expand based on content
          minWidth: props.anchorEl?.clientWidth, // At least as wide as the input
          maxWidth: '500px', // Set a maximum width if needed
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* Dropdowns stacked vertically */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <FormControl
          sx={{
            width: '100%',
            height: 50,
          }}
        >
          <Autocomplete
            id="project"
            value={newTimecard.project || null}
            onChange={(event, newValue) => {
              const syntheticEvent = {
                target: {
                  name: 'project',
                  value: newValue,
                },
              };
              handleChange(syntheticEvent);
            }}
            disablePortal={false}
            PopperComponent={CustomPopper}
            options={activeProjects}
            getOptionLabel={(option) =>
              option ? `${option.number} - ${option.name}` : ''
            }
            isOptionEqualToValue={(option, value) =>
              option && value ? option.id === value.id : false
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {`${option.number} - ${option.name}`}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Project" />}
          />
        </FormControl>

        <FormControl
          sx={{
            width: '100%',
            height: 50,
          }}
        >
          <Autocomplete
            id="packageArea"
            value={selectedArea || null}
            onChange={(event, newValue) => {
              const syntheticEvent = {
                target: {
                  name: 'packageArea',
                  value: newValue,
                },
              };
              handleChange(syntheticEvent);
            }}
            disablePortal={false}
            PopperComponent={CustomPopper}
            options={filteredAreas || []}
            getOptionLabel={(option) => (option ? option.name : '')}
            isOptionEqualToValue={(option, value) =>
              option && value ? option.id === value.id : false
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Area" />}
          />
        </FormControl>

        <FormControl
          sx={{
            width: '100%',
            height: 50,
          }}
        >
          <Autocomplete
            id="package"
            value={newTimecard.package || null}
            onChange={(event, newValue) => {
              const syntheticEvent = {
                target: {
                  name: 'package',
                  value: newValue,
                },
              };
              handleChange(syntheticEvent);
            }}
            disablePortal={false}
            PopperComponent={CustomPopper}
            options={filteredPackages}
            getOptionLabel={(option) =>
              option
                ? option.autodesk_name?.trim()
                  ? `${option.autodesk_name} - ${option.package_name}`
                  : option.package_name
                : ''
            }
            isOptionEqualToValue={(option, value) =>
              option && value ? option.package_id === value.package_id : false
            }
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.package_id}
                style={option.archived ? { color: '#999' } : {}}
              >
                {option.autodesk_name?.trim()
                  ? `${option.autodesk_name} - ${option.package_name}`
                  : option.package_name}
                {option.archived && (
                  <Chip
                    label="Archived"
                    size="small"
                    sx={{
                      ml: 1.5,
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      backgroundColor: '#e0e0e0',
                      color: '#616161',
                    }}
                  />
                )}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Package" />}
          />
        </FormControl>

        {/* Only show activity dropdown if package has include_activities = true */}
        {newTimecard.package?.include_activities !== false && (
          <FormControl
            sx={{
              width: '100%',
              height: 50,
            }}
          >
            <Autocomplete
              id="activity"
              value={newTimecard.activity || null}
              onChange={(event, newValue) => {
                const syntheticEvent = {
                  target: {
                    name: 'activity',
                    value: newValue,
                  },
                };
                handleChange(syntheticEvent);
              }}
              disablePortal={false}
              PopperComponent={CustomPopper}
              options={packageAct.filter(
                (a) => a.activity_name?.toLowerCase() !== 'default'
              )}
              getOptionLabel={(option) => (option ? option.activity_name : '')}
              isOptionEqualToValue={(option, value) =>
                option && value
                  ? option.activity_id === value.activity_id
                  : false
              }
              renderOption={(props, option) => (
                <li {...props} key={option.activity_id}>
                  {option.activity_name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Activity" />
              )}
            />
          </FormControl>
        )}
      </Box>

      {/* Hour inputs and Add button on single line */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap', // Always allow wrapping
          minWidth: 0, // Allows container to shrink
          overflow: 'hidden', // Prevents content from overflowing
        }}
      >
        <Box sx={{ minWidth: '100px', flexShrink: 0 }}>
          <NumericTextBoxComponent
            ref={hoursStRef}
            id="hours_st"
            name="hours_st"
            cssClass={'e-style'}
            decimals={1}
            format="n1"
            width="100px"
            min={0}
            value={newTimecard.hours_st}
            step={0.5}
            placeholder="ST"
            floatLabelType="Always"
            onChange={handleChange}
            onBlur={(e) => handleNumericBlur('hours_st', e)}
            htmlAttributes={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />
        </Box>

        <Box sx={{ minWidth: '100px', flexShrink: 0 }}>
          <NumericTextBoxComponent
            ref={hoursOtRef}
            id="hours_ot"
            name="hours_ot"
            cssClass={'e-style'}
            decimals={1}
            format="n1"
            width="100px"
            min={0}
            value={newTimecard.hours_ot}
            step={0.5}
            placeholder="OT"
            floatLabelType="Always"
            onChange={handleChange}
            onBlur={(e) => handleNumericBlur('hours_ot', e)}
            htmlAttributes={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />
        </Box>

        <Box sx={{ minWidth: '100px', flexShrink: 0 }}>
          <NumericTextBoxComponent
            ref={hoursDtRef}
            id="hours_dt"
            name="hours_dt"
            cssClass={'e-style'}
            decimals={1}
            format="n1"
            width="100px"
            min={0}
            value={newTimecard.hours_dt}
            step={0.5}
            placeholder="DT"
            floatLabelType="Always"
            onChange={handleChange}
            onBlur={(e) => handleNumericBlur('hours_dt', e)}
            htmlAttributes={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          />
        </Box>

        <Box
          sx={{
            minWidth: '80px',
            maxWidth: '100px',
            overflow: 'visible',
            paddingTop: '10px',
            flexShrink: 0,
          }}
        >
          <FormControl
            fullWidth
            sx={{
              '& .MuiInputBase-root': {
                height: '54px !important',
                minHeight: '54px !important',
                maxHeight: '54px !important',
              },
              '& .MuiOutlinedInput-root': {
                height: '54px !important',
                minHeight: '54px !important',
                maxHeight: '54px !important',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderTop: '1px solid rgba(0, 0, 0, 0.87) !important',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderTop: '2px solid #3f51b5 !important',
                },
              },
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, -12px) scale(0.75) !important',
                top: '0px !important',
                left: '0px !important',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -12px) scale(0.75) !important',
                  top: '0px !important',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                top: '0px !important',
                borderTop: '1px solid rgba(0, 0, 0, 0.23) !important',
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                maxWidth: '0px !important',
              },
              '& .MuiSelect-select': {
                paddingTop: '16.5px !important',
                paddingBottom: '16.5px !important',
                paddingLeft: '14px !important',
                paddingRight: '32px !important',
                height: 'auto !important',
                lineHeight: '1.4375em !important',
              },
            }}
          >
            <InputLabel
              id="shift-label"
              shrink={true}
              sx={{ top: '-12px', left: '14px' }}
            >
              Shift
            </InputLabel>
            <Select
              labelId="shift-label"
              id="shift"
              name="shift"
              label="Shift"
              value={newTimecard.shift || ''}
              onChange={handleChange}
              notched={false}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="2nd">2nd</MenuItem>
              <MenuItem value="3rd">3rd</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Fixed width: the field's natural width follows the font (~20 characters),
            so with Inter it grew and pushed the copy button onto the next line. */}
        <Box
          sx={{
            width: '200px',
            minWidth: '150px',
            flexShrink: 0,
            paddingTop: '10px',
          }}
        >
          <TextField
            id="exception"
            name="exception"
            label="Exception"
            value={newTimecard.exception || ''}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                height: '54px !important',
                minHeight: '54px !important',
                maxHeight: '54px !important',
              },
              '& .MuiOutlinedInput-root': {
                height: '54px !important',
                minHeight: '54px !important',
                maxHeight: '54px !important',
              },
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, -12px) scale(0.75) !important',
                top: '0px !important',
                left: '0px !important',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -12px) scale(0.75) !important',
                  top: '0px !important',
                },
              },
              '& .MuiOutlinedInput-input': {
                paddingTop: '16.5px !important',
                paddingBottom: '16.5px !important',
                paddingLeft: '14px !important',
                paddingRight: '14px !important',
                height: 'auto !important',
                lineHeight: '1.4375em !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                top: '0px !important',
              },
            }}
          />
        </Box>

        <Tooltip title="Add Item to Employee's Timecard">
          <IconButton
            color="primary"
            onClick={handleSubmit}
            sx={{
              height: 50,
              width: 50,
              alignSelf: 'flex-end',
              marginTop: '8px',
              flexShrink: 0,
            }}
          >
            <PlaylistAddIcon sx={{ fontSize: 40 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Previous Day Timecard Entries">
          <IconButton
            color="primary"
            onClick={() => setCopyPreviousDialogOpen(true)}
            sx={{
              height: 50,
              width: 50,
              alignSelf: 'flex-end',
              marginTop: '8px',
              flexShrink: 0,
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 35 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Payroll Notes field below the hour inputs */}
      <Box sx={{ width: '100%' }}>
        <TextField
          id="payrollNotes"
          name="payrollNotes"
          label="Payroll Notes"
          value={newTimecard.payrollNotes || ''}
          onChange={handleChange}
          fullWidth
          multiline
          rows={2}
          variant="outlined"
        />
      </Box>

      <AlertDialog />
      <CopyPreviousEntryDialog
        open={copyPreviousDialogOpen}
        onClose={() => setCopyPreviousDialogOpen(false)}
        onWithHours={() => copyPreviousDayTimecards(true)}
        onWithoutHours={() => copyPreviousDayTimecards(false)}
      />
    </Box>
  );
};

export default TimecardAddItem;
